import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Check, X, Flame, Swords, Target, Info, Users,
  UserPlus, CheckCircle, AtSign, Shield, Trash2, Settings,
} from "lucide-react";
import { useTheme } from "@/lib/ThemeContext";
import { useAuth } from "@/lib/AuthContext";
import NavTooltip from "@/components/NavTooltip";
import logger from "@/lib/logger";
import { track } from "@/lib/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_ICONS = {
  streak_reminder: Flame,
  quest_reminder: Target,
  boss_reminder: Swords,
  project_invite: UserPlus,
  invite_accepted: CheckCircle,
  task_assigned: CheckCircle,
  role_changed: Shield,
  removed_from_project: Users,
  mention: AtSign,
  info: Info,
};

const FILTER_TABS = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "mentions", label: "Mentions" },
];

export default function NotificationCenter() {
  const { tokens } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState("all");

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${API}/api/notifications?limit=30`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (e) { logger.error("fetchNotifications:", e); }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/api/notifications/check-reminders`, { method: "POST", credentials: "include" })
      .then(() => fetchNotifications())
      .catch((e) => logger.error("checkReminders:", e));
  }, [user, fetchNotifications]);

  // CommandPalette → "Open notifications" dispatches a global event.
  // Open the dropdown so the user can browse without clicking the bell.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("mmntm:open-notifications", handler);
    return () => window.removeEventListener("mmntm:open-notifications", handler);
  }, []);

  const markRead = async (id) => {
    await fetch(`${API}/api/notifications/${id}/read`, { method: "PUT", credentials: "include" });
    fetchNotifications();
  };

  const markAllRead = async () => {
    await fetch(`${API}/api/notifications/read-all`, { method: "PUT", credentials: "include" });
    fetchNotifications();
  };

  const dismiss = async (id) => {
    await fetch(`${API}/api/notifications/${id}`, { method: "DELETE", credentials: "include" });
    fetchNotifications();
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    // U06a: task reminders link straight to the task
    if (n.link && n.task_id) {
      track("reminder_opened", { task_id: n.task_id, kind: n.type === "task_overdue" ? "overdue" : "due", channel: "inapp" });
      navigate(n.link);
      setOpen(false);
      return;
    }
    // Navigate based on type
    if (n.project_id) {
      navigate(`/?project=${n.project_id}`);
    }
    setOpen(false);
  };

  if (!user) return null;

  const filtered = notifications.filter(n => {
    if (filter === "unread") return !n.read;
    if (filter === "mentions") return n.type === "mention" || n.type === "task_assigned";
    return true;
  });

  const timeAgo = (iso) => {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return "now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <div className="relative" data-testid="notification-center">
      <NavTooltip label="Notifications">
        <button
          onClick={() => setOpen(!open)}
          className="relative p-2 rounded-lg transition-colors"
          style={{ color: tokens.textMuted }}
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
          data-testid="notification-bell"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center"
              style={{ background: tokens.accent, color: tokens.bgPrimary }}
              data-testid="notification-badge" aria-label={`${unreadCount} unread notifications`}>
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </NavTooltip>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-80 max-h-[480px] rounded-xl border z-50 shadow-2xl flex flex-col"
            style={{ background: tokens.bgPrimary, borderColor: tokens.borderColor }}
            role="dialog" aria-label="Notifications"
            data-testid="notification-dropdown">

            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b shrink-0" style={{ borderColor: tokens.borderColor }}>
              <span className="text-xs font-semibold" style={{ color: tokens.textPrimary }}>Notifications</span>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-[10px] px-2 py-0.5 rounded"
                    style={{ color: tokens.accent, background: `${tokens.accent}10` }}
                    data-testid="mark-all-read">Mark all read</button>
                )}
                <button onClick={() => { setOpen(false); navigate("/settings/notifications"); }}
                  className="p-0.5 rounded" style={{ color: tokens.textMuted }} aria-label="Notification settings">
                  <Settings className="h-3 w-3" />
                </button>
              </div>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-0.5 px-2 py-1.5 shrink-0" style={{ borderBottom: `1px solid ${tokens.borderColor}` }}>
              {FILTER_TABS.map(tab => (
                <button key={tab.id} onClick={() => setFilter(tab.id)}
                  className="px-2.5 py-1 text-[10px] font-semibold rounded-md transition-colors"
                  style={{
                    background: filter === tab.id ? `${tokens.accent}12` : "transparent",
                    color: filter === tab.id ? tokens.accent : tokens.textMuted,
                  }}
                  data-testid={`notif-filter-${tab.id}`}>
                  {tab.label}
                  {tab.id === "unread" && unreadCount > 0 && (
                    <span className="ml-1 text-[9px]">{unreadCount}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Notification list */}
            <div className="overflow-y-auto flex-1" role="list">
              {filtered.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Bell className="h-6 w-6 mx-auto mb-2 opacity-30" style={{ color: tokens.textMuted }} />
                  <p className="text-xs" style={{ color: tokens.textMuted }}>
                    {filter === "unread" ? "All caught up!" : filter === "mentions" ? "No mentions yet" : "No notifications yet"}
                  </p>
                </div>
              ) : (
                filtered.map((n) => {
                  const Icon = TYPE_ICONS[n.type] || Info;
                  return (
                    <div key={n.id}
                      className="px-3 py-2.5 flex gap-2.5 border-b last:border-0 transition-colors cursor-pointer"
                      style={{
                        borderColor: `${tokens.borderColor}50`,
                        background: n.read ? "transparent" : `${tokens.accent}05`,
                      }}
                      onClick={() => handleClick(n)}
                      role="listitem"
                      data-testid={`notification-item-${n.id}`}>
                      <div className="shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ background: n.read ? `${tokens.textMuted}10` : `${tokens.accent}12` }}>
                        <Icon className="h-3 w-3" style={{ color: n.read ? tokens.textMuted : tokens.accent }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] font-medium" style={{ color: tokens.textPrimary }}>{n.title}</p>
                        <p className="text-[10px] mt-0.5 line-clamp-2" style={{ color: tokens.textMuted }}>{n.message}</p>
                        <span className="text-[9px] mt-0.5 block tabular-nums" style={{ color: `${tokens.textMuted}80`, fontFamily: "'JetBrains Mono', monospace" }}>
                          {timeAgo(n.created_at)}
                        </span>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        {!n.read && (
                          <button onClick={(e) => { e.stopPropagation(); markRead(n.id); }} className="p-0.5 rounded hover:opacity-80"
                            aria-label="Mark as read" data-testid={`notif-read-${n.id}`}>
                            <Check className="h-3 w-3" style={{ color: tokens.accent }} />
                          </button>
                        )}
                        <button onClick={(e) => { e.stopPropagation(); dismiss(n.id); }} className="p-0.5 rounded hover:opacity-80"
                          aria-label="Dismiss" data-testid={`notif-dismiss-${n.id}`}>
                          <X className="h-3 w-3" style={{ color: tokens.textMuted }} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
